import { PowerSettingsNew } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import { findIndex } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  CrudPage,
  CrudTableActionType,
  DataGridColumnDefinition,
  DateLabel,
  EnabledStatusLabel,
  SidebarLayout,
  SidebarSize,
} from 'components';
import { FilterDefinitionType } from 'enums';
import { useProviderTypes, useUser } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { DynamicAdapterConfig, FilterDefinition } from 'types';
import { formatUtils } from 'utils';

import { DynamicAdapterConfigDetails } from './DynamicAdapterConfigDetails';
import { DynamicAdapterConfigDetailsDialog } from './DynamicAdapterConfigDetailsDialog';
import { useDynamicAdapterConfigs } from './hooks/useDynamicAdapterConfigs';

type DynamicAdapterConfigFilters = {
  id?: string;
  name?: string;
  providerType?: string;
  isActive?: boolean;
};

const KEYS = {
  title: 'pages.dynamic_adapter_configs.title',
  details_title: 'pages.dynamic_adapter_configs.details.title',
  fields: {
    name: 'pages.dynamic_adapter_configs.fields.name',
    provider_type: 'pages.dynamic_adapter_configs.fields.provider_type',
    version: 'pages.dynamic_adapter_configs.fields.version',
    status: 'pages.dynamic_adapter_configs.fields.status',
    config_type: 'pages.dynamic_adapter_configs.fields.config_type',
    created_at: 'pages.dynamic_adapter_configs.fields.created_at',
    updated_at: 'pages.dynamic_adapter_configs.fields.updated_at',
  },
} as const;

type Props = {
  onCreateRef?: React.MutableRefObject<(() => void) | null>;
};

export const DynamicAdapterConfigsPage: React.FC<Props> = ({ onCreateRef }) => {
  const { isAdmin, isTechOperator } = useUser();
  const { t: tAdmin } = useTranslation(TranslationNamespace.Admin);
  const t = tAdmin as unknown as (key: string) => string;
  const { t: tCommon } = useTranslation(TranslationNamespace.Common);

  const [selectedConfig, setSelectedConfig] =
    useState<DynamicAdapterConfig | null>(null);
  const [dialogData, setDialogData] = useState<{
    open: boolean;
    data?: DynamicAdapterConfig;
  }>({ open: false });

  const {
    queryResult,
    removeMutation,
    activateMutation,
    deactivateMutation,
    cloneMutation,
  } = useDynamicAdapterConfigs();

  const { providerTypeOptions } = useProviderTypes();

  const getProviderTypeLabel = useCallback(
    (code: string) => {
      const option = providerTypeOptions.find(
        (opt: { value: string; label: string }) => opt.value === code,
      );
      return option?.label || code;
    },
    [providerTypeOptions],
  );

  const columns = useMemo(
    (): DataGridColumnDefinition<DynamicAdapterConfig>[] => [
      {
        header: t(KEYS.fields.name),
        valueGetter: (item) => item.name,
        valueClassName: 'tw-font-medium',
      },
      {
        header: t(KEYS.fields.provider_type),
        valueGetter: (item) => getProviderTypeLabel(item.providerType),
      },
      {
        header: t(KEYS.fields.version),
        valueGetter: (item) => item.version,
      },
      {
        header: t(KEYS.fields.status),
        valueGetter: (item) => (
          <EnabledStatusLabel status={item.isActive ? 'active' : 'inactive'} />
        ),
      },
      {
        header: t(KEYS.fields.config_type),
        valueGetter: (item) => {
          const hasPayin = !!item.payinConfig;
          const hasPayout = !!item.payoutConfig;
          if (hasPayin && hasPayout) {
            return `${tCommon('common.payin')} + ${tCommon('common.payout')}`;
          }
          if (hasPayin) {
            return tCommon('common.payin');
          }
          if (hasPayout) {
            return tCommon('common.payout');
          }
          return '-';
        },
      },
      {
        header: t(KEYS.fields.created_at),
        valueGetter: (item) => (
          <DateLabel>{formatUtils.formatDate(item.createdAt)}</DateLabel>
        ),
      },
      {
        header: t(KEYS.fields.updated_at),
        valueGetter: (item) => (
          <DateLabel>{formatUtils.formatDate(item.updatedAt)}</DateLabel>
        ),
      },
    ],
    [t, tCommon, getProviderTypeLabel],
  );

  const handleSelectConfig = useCallback((config: DynamicAdapterConfig) => {
    setSelectedConfig(config);
  }, []);

  const handleDeselectConfig = useCallback(() => {
    setSelectedConfig(null);
  }, []);

  const handleCreate = useCallback(() => {
    setDialogData({ open: true });
  }, []);

  useEffect(() => {
    if (onCreateRef) {
      onCreateRef.current = handleCreate;
    }
  }, [onCreateRef, handleCreate]);

  const handleEdit = useCallback((config: DynamicAdapterConfig) => {
    setDialogData({ open: true, data: config });
  }, []);

  const handleDialogClose = useCallback(() => {
    setDialogData({ open: false });
  }, []);

  const handleClone = useCallback(
    (config: DynamicAdapterConfig) => {
      const newName = `${config.name} (copy)`;
      cloneMutation.mutate({ id: config.id, name: newName });
    },
    [cloneMutation],
  );

  const handleToggleActive = useCallback(
    (config: DynamicAdapterConfig) => {
      if (config.isActive) {
        deactivateMutation.mutate(config.id);
      } else {
        activateMutation.mutate(config.id);
      }
    },
    [activateMutation, deactivateMutation],
  );

  const selectedRowIndex = useMemo(
    () =>
      findIndex(queryResult?.data?.items, {
        id: selectedConfig?.id,
      }),
    [queryResult, selectedConfig],
  );

  const filtersDefinitions: FilterDefinition<DynamicAdapterConfigFilters>[] =
    useMemo(
      () => [
        {
          label: tCommon('filters.id'),
          name: 'id',
          type: FilterDefinitionType.Text,
          format: 'uuid',
        },
        {
          label: tCommon('filters.name'),
          name: 'name',
          type: FilterDefinitionType.Text,
        },
        {
          label: tCommon('filters.type'),
          name: 'providerType',
          type: FilterDefinitionType.Select,
          options: providerTypeOptions,
          getDisplayName: getProviderTypeLabel,
        },
        {
          label: t(KEYS.fields.status),
          name: 'isActive',
          type: FilterDefinitionType.Select,
          options: [
            { value: 'true', label: tCommon('common.enabled') },
            { value: 'false', label: tCommon('common.disabled') },
          ],
        },
      ],
      [tCommon, t, providerTypeOptions, getProviderTypeLabel],
    );

  return (
    <>
      <SidebarLayout
        sidebarProps={{
          open: !!selectedConfig,
          title: t(KEYS.details_title),
          size: SidebarSize.md,
          onClose: handleDeselectConfig,
          content: selectedConfig && (
            <DynamicAdapterConfigDetails
              config={selectedConfig}
              onEdit={() => handleEdit(selectedConfig)}
              onToggleActive={() => handleToggleActive(selectedConfig)}
              onClone={() => handleClone(selectedConfig)}
            />
          ),
        }}
      >
        <CrudPage
          header={{
            divider: false,
            ...(!onCreateRef &&
              (isAdmin || isTechOperator) && {
                rightContentButton: { onClick: handleCreate },
              }),
          }}
          filters={{ filtersDefinitions }}
          table={{
            queryResult,
            columns,
            paginated: true,
            actions: [
              {
                type: CrudTableActionType.Details,
                onClick: handleEdit,
              },
              {
                type: CrudTableActionType.Custom,
                render: (item: DynamicAdapterConfig) => (
                  <IconButton
                    title={formatUtils.formatEnabled(!item.isActive)}
                    color={item.isActive ? 'warning' : 'success'}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleActive(item);
                    }}
                  >
                    <PowerSettingsNew />
                  </IconButton>
                ),
              },
              {
                type: CrudTableActionType.Remove,
                onRemove: (item, { close }) =>
                  removeMutation.mutate(item.id, { onSuccess: close }),
              },
            ],
            onRowClick: handleSelectConfig,
            selectedRowIndex,
          }}
        />
      </SidebarLayout>

      <DynamicAdapterConfigDetailsDialog
        open={dialogData.open}
        data={dialogData.data}
        onClose={handleDialogClose}
      />
    </>
  );
};
